import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  BarChart2, Plus, Trash2, Edit2, ChevronDown, ChevronUp, Download, RefreshCw, X, Save,
  AlertTriangle, CheckCircle, Clock, Activity, Upload, HeartPulse, Brain, HeartHandshake,
  Pill, Settings2, Search, Check, Zap, History,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useClientRecord, ClientFrame } from './ClientLayout';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import { csvCell } from '../../utils/csv';

// ── Category look ─────────────────────────────────────────────────────────────
const CATEGORY_META = {
  health_medical:    { label: 'Health and medical', icon: HeartPulse,     badge: 'bg-blue-100 text-blue-700',       tint: 'bg-blue-50',    ring: 'border-blue-200' },
  behaviour_support: { label: 'Behaviour support',  icon: Brain,          badge: 'bg-purple-100 text-purple-700',   tint: 'bg-purple-50',  ring: 'border-purple-200' },
  personal_care:     { label: 'Personal care',      icon: HeartHandshake, badge: 'bg-emerald-100 text-emerald-700', tint: 'bg-emerald-50', ring: 'border-emerald-200' },
};
const CATS = Object.keys(CATEGORY_META);
const catMeta = (c) => CATEGORY_META[c] || { label: 'Other', icon: Activity, badge: 'bg-slate-100 text-slate-700', tint: 'bg-slate-50', ring: 'border-slate-200' };

// ── Time helpers ──────────────────────────────────────────────────────────────
// recorded_at is a bare TIMESTAMP holding UTC wall-clock, so the server also sends the instant as
// epoch milliseconds (recorded_ms / last_entry_ms); every display and comparison uses that.
const entryMs = (e) => Number(e?.recorded_ms) || (e?.recorded_at ? new Date(e.recorded_at).getTime() : 0);
const fmtDT = (ms) => (ms ? new Date(ms).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const clockOf = (ms) => new Date(ms).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
const nowHHMM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const toLocalInput = (ms) => new Date(ms - new Date(ms).getTimezoneOffset() * 60000).toISOString().slice(0, 16);
const fromLocalInput = (s) => new Date(s).toISOString();
const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
function ago(ms, now = Date.now()) {
  const m = Math.round((now - ms) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

// The chart's frequency is free text ("Every 4 hours", "Daily", "PRN"). Only the ones that name an
// interval get a due time; the same reading as the carer app so both screens agree.
function frequencyHours(freq) {
  const f = String(freq || '').toLowerCase();
  if (!f.trim() || /\b(prn|as required|as needed|when required|each shift|every shift|per shift)\b/.test(f)) return null;
  let m = f.match(/every\s*(\d+(?:\.\d+)?)\s*(h|hr|hrs|hour|hours)\b/);
  if (m) return Number(m[1]) || null;
  m = f.match(/every\s*(\d+)\s*(min|mins|minutes)\b/);
  if (m) return Number(m[1]) / 60 || null;
  if (/\bhourly\b|every hour/.test(f)) return 1;
  if (/\b(qid|four times)\b/.test(f)) return 6;
  if (/\b(tds|tid|three times)\b/.test(f)) return 8;
  if (/\b(bd|bid|twice)\b/.test(f)) return 12;
  m = f.match(/(\d+)\s*(x|times)\s*(a|per)?\s*day/);
  if (m && Number(m[1])) return 24 / Number(m[1]);
  if (/\b(daily|once a day|every day|each day|nightly|every morning|every night)\b/.test(f)) return 24;
  if (/\bweekly\b|once a week|every week/.test(f)) return 168;
  return null;
}
function dueStatus(freq, lastMs, now = Date.now()) {
  const hrs = frequencyHours(freq);
  const last = lastMs ? `Last recorded ${ago(lastMs, now)}` : 'Nothing recorded yet';
  if (!hrs) return { text: last, tone: 'neutral' };
  if (!lastMs) return { text: `${last} · due now`, tone: 'warning', due: true };
  const dueAt = lastMs + hrs * 3600000;
  if (now < dueAt) return { text: `${last} · next due ${clockOf(dueAt)}${sameDay(dueAt, now) ? '' : ' tomorrow'}`, tone: 'neutral' };
  if (now < dueAt + hrs * 3600000) return { text: `${last} · due since ${clockOf(dueAt)}`, tone: 'warning', due: true };
  return { text: `${last} · overdue`, tone: 'danger', due: true, overdue: true };
}
const TONE = {
  neutral: 'text-slate-500',
  warning: 'text-amber-700',
  danger: 'text-red-600 font-semibold',
};

const FREQ_PRESETS = ['Every shift', 'Every 2 hours', 'Every 4 hours', 'Twice daily', 'Daily', 'Weekly', 'As needed'];

function initialsOf(user) {
  const first = user?.firstName || user?.first_name || (user?.name || '').split(' ')[0] || '';
  const last = user?.lastName || user?.last_name || (user?.name || '').split(' ').slice(1).join(' ') || '';
  return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
}

function downloadCSV(name, headers, rows) {
  // csvCell neutralises cells Excel would run as formulas (staff-typed text ends up here).
  const csv = [headers, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${name.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Ranges, smart field config ────────────────────────────────────────────────
const FIELD_RANGES = {
  reading:           { normal: [4, 8],       critical: [3, 15],     hint: 'Normal 4–8 mmol/L' },
  temperature:       { normal: [36.1, 37.2], critical: [35, 38.5],  hint: 'Normal 36.1–37.2°C' },
  pulse:             { normal: [60, 100],    critical: [40, 150],   hint: 'Normal 60–100 bpm' },
  oxygen_saturation: { normal: [95, 100],    critical: [90, 100],   hint: 'Normal 95% or more' },
  bp_systolic:       { normal: [90, 140],    critical: [80, 180],   hint: 'Normal 90–140' },
  bp_diastolic:      { normal: [60, 90],     critical: [50, 110],   hint: 'Normal 60–90' },
  respiratory_rate:  { normal: [12, 20],     critical: [8, 30],     hint: 'Normal 12–20 /min' },
};
function getRangeStatus(key, val) {
  const r = FIELD_RANGES[key];
  if (!r || val === '' || val == null) return null;
  const n = Number(val);
  if (Number.isNaN(n)) return null;
  if (n < r.critical[0] || n > r.critical[1]) return { label: n < r.critical[0] ? 'Critically low' : 'Critically high', cls: 'text-red-600 font-bold', cell: 'bg-red-100 text-red-700 font-bold' };
  if (n < r.normal[0] || n > r.normal[1]) return { label: n < r.normal[0] ? 'Below normal' : 'Above normal', cls: 'text-amber-600 font-semibold', cell: 'bg-amber-100 text-amber-800 font-semibold' };
  return { label: 'Normal', cls: 'text-emerald-600 font-semibold', cell: '' };
}

const CHART_CONFIG = {
  blood_glucose:         { conditionals: { insulin_dose: d => !!d.insulin_given } },
  vital_signs:           { pairedFields: [['bp_systolic', 'bp_diastolic']] },
  seizure_chart:         { banner: 'Document straight away, and notify the supervisor and family as the emergency care plan says.' },
  restrictive_practices: { banner: 'NDIS reporting: an unauthorised restrictive practice is a reportable incident. This log may be reviewed by the NDIS Commission.' },
  behaviour_frequency:   { counterFields: new Set(['frequency']) },
  sleep_chart: {
    autoCalc: {
      total_hours: d => {
        if (!d.bed_time || !d.wake_time) return null;
        const [bh, bm] = d.bed_time.split(':').map(Number);
        const [wh, wm] = d.wake_time.split(':').map(Number);
        let mins = (wh * 60 + wm) - (bh * 60 + bm);
        if (mins < 0) mins += 1440;
        return Math.round(mins / 6) / 10;
      },
    },
  },
  mealtime_management: { prominentFields: new Set(['choking_episode']) },
  hygiene_chart:       { boolGroup: ['hair_wash', 'oral_care', 'skin_care', 'nail_care'] },
};

// The fields shown in the one-step "Quick record" row on each chart. Time-of-day fields are filled
// with the current time; everything else lives in "All fields". A chart missing here (seizures,
// restrictive practices, wounds, ABC) always opens the full form, because those records need care.
const QUICK_FIELDS = {
  blood_glucose:       ['reading', 'pre_post_meal', 'insulin_given', 'insulin_dose'],
  vital_signs:         ['temperature', 'pulse', 'bp_systolic', 'bp_diastolic', 'oxygen_saturation', 'respiratory_rate'],
  weight_monitoring:   ['weight_kg'],
  food_fluid_intake:   ['meal_time', 'food_consumed', 'fluid_ml'],
  bowel_chart:         ['stool_type', 'amount', 'continent'],
  pain_assessment:     ['pain_scale', 'location', 'pain_type'],
  sleep_chart:         ['bed_time', 'wake_time', 'quality'],
  behaviour_frequency: ['behaviour_type', 'frequency'],
  mood_wellbeing:      ['mood_scale', 'mood_description', 'social_engagement'],
  hygiene_chart:       ['shower_bath', 'hair_wash', 'oral_care', 'skin_care', 'nail_care', 'assistance_level'],
  continence_chart:    ['type', 'continent', 'pad_change'],
  repositioning_chart: ['position', 'skin_condition', 'pressure_area_check'],
  mealtime_management: ['meal_time', 'texture', 'assistance_level', 'choking_episode'],
};
const AUTO_TIME_KEYS = new Set(['time', 'actual_time']);

function missingRequired(chart, data) {
  const cfg = CHART_CONFIG[chart.slug] || {};
  return (chart.fields || []).filter(f => {
    if (!f.required) return false;
    if (cfg.conditionals?.[f.key] && !cfg.conditionals[f.key](data)) return false;
    return data[f.key] === '' || data[f.key] === undefined || data[f.key] === null;
  });
}

// ── Field renderer ────────────────────────────────────────────────────────────
function DynamicField({ field, value, onChange, isCounter, compact }) {
  const base = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition bg-white';

  if (field.type === 'boolean') {
    return (
      <div className="flex gap-2">
        {[true, false].map(v => (
          <button key={String(v)} type="button" onClick={() => onChange(value === v ? undefined : v)}
            className={`flex-1 ${compact ? 'py-1.5' : 'py-2'} rounded-xl border-2 text-sm font-semibold transition-all ${
              value === v ? (v ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-slate-500 border-slate-500 text-white')
                : 'border-slate-200 text-slate-500 hover:border-slate-300 bg-white'}`}>
            {v ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    );
  }

  if (field.type === 'scale') {
    const num = value === undefined || value === '' ? null : Number(value);
    const isMood = field.key === 'mood_scale';
    const MOOD_EMOJI = ['', '😔', '😟', '😕', '😞', '😐', '🙂', '😊', '😄', '😁', '🤩'];
    const label = (n) => field.key === 'pain_scale'
      ? (n === 0 ? 'No pain' : n <= 3 ? 'Mild' : n <= 6 ? 'Moderate' : 'Severe')
      : isMood ? (n <= 3 ? 'Low mood' : n <= 7 ? 'Fair mood' : 'Good mood')
      : (n === 0 ? 'None' : n <= 3 ? 'Mild' : n <= 6 ? 'Moderate' : 'Severe');
    const tone = (n) => isMood
      ? (n <= 3 ? 'bg-red-500 border-red-500' : n <= 6 ? 'bg-amber-500 border-amber-500' : 'bg-emerald-500 border-emerald-500')
      : (n === 0 ? 'bg-slate-400 border-slate-400' : n <= 3 ? 'bg-emerald-500 border-emerald-500' : n <= 6 ? 'bg-amber-500 border-amber-500' : 'bg-red-500 border-red-500');
    return (
      <div>
        <div className="flex flex-wrap gap-1.5">
          {(isMood ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]).map(n => (
            <button key={n} type="button" onClick={() => onChange(n)} title={`${n}: ${label(n)}`}
              className={`${compact ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm'} rounded-xl font-bold border-2 transition-all select-none ${
                num === n ? `${tone(n)} text-white shadow-sm` : 'border-slate-200 text-slate-500 hover:bg-slate-50 bg-white'}`}>
              {isMood ? MOOD_EMOJI[n] : n}
            </button>
          ))}
        </div>
        {num !== null && <p className="text-xs font-semibold mt-1 text-slate-600">{label(num)} ({num}/10)</p>}
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className="flex flex-wrap gap-1.5">
        {field.options?.map(opt => (
          <button key={opt} type="button" onClick={() => onChange(value === opt ? undefined : opt)}
            className={`px-3 ${compact ? 'py-1' : 'py-1.5'} rounded-xl text-xs font-semibold border-2 transition-all ${
              value === opt ? 'bg-primary-500 border-primary-500 text-white shadow-sm' : 'border-slate-200 text-slate-600 hover:border-primary-300 hover:bg-primary-50 bg-white'}`}>
            {opt}
          </button>
        ))}
      </div>
    );
  }

  if (field.type === 'textarea') {
    return <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={3} className={`${base} resize-none`} placeholder={field.label} />;
  }

  if (isCounter) {
    const n = Number(value) || 0;
    return (
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => onChange(Math.max(0, n - 1))} className="w-9 h-9 rounded-xl border-2 border-slate-200 text-slate-700 text-lg font-bold hover:bg-slate-50">−</button>
        <span className="text-2xl font-bold text-slate-800 w-12 text-center tabular-nums">{n}</span>
        <button type="button" onClick={() => onChange(n + 1)} className="w-9 h-9 rounded-xl border-2 border-slate-200 text-slate-700 text-lg font-bold hover:bg-slate-50">+</button>
        <span className="text-xs text-slate-400">times</span>
      </div>
    );
  }

  const range = getRangeStatus(field.key, value);
  return (
    <div>
      <input
        type={field.type === 'number' ? 'number' : field.type === 'time' ? 'time' : field.type === 'date' ? 'date' : 'text'}
        inputMode={field.type === 'number' ? 'decimal' : undefined}
        value={value ?? ''}
        onChange={e => onChange(field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
        className={base} placeholder={FIELD_RANGES[field.key]?.hint || field.label} step={field.type === 'number' ? 'any' : undefined}
      />
      {range && <p className={`text-xs mt-1 ${range.cls}`}>{range.label}</p>}
    </div>
  );
}

// ── Full entry form (slide-over) ──────────────────────────────────────────────
function EntryFormPanel({ chart, entry, initial, onClose, onSaved }) {
  const { id: clientId } = useParams();
  const cfg = CHART_CONFIG[chart.slug] || {};
  const [formData, setFormData] = useState(() => {
    if (entry?.data) return entry.data;
    const d = { ...(initial || {}) };
    (chart.fields || []).forEach(f => { if (f.type === 'time' && AUTO_TIME_KEYS.has(f.key) && !d[f.key]) d[f.key] = nowHHMM(); });
    return d;
  });
  const [notes, setNotes] = useState(entry?.notes || '');
  const [recordedAt, setRecordedAt] = useState(toLocalInput(entry ? entryMs(entry) : Date.now()));
  const [saving, setSaving] = useState(false);

  const setField = (key, val) => setFormData(prev => {
    const next = { ...prev, [key]: val };
    if (cfg.autoCalc) for (const [k, fn] of Object.entries(cfg.autoCalc)) { const r = fn(next); if (r !== null) next[k] = r; }
    return next;
  });

  async function submit(again) {
    const missing = missingRequired(chart, formData);
    if (missing.length) { toast.error(`Still needed: ${missing.map(f => f.label).join(', ')}`); return; }
    setSaving(true);
    try {
      const body = { data: formData, notes, recorded_at: fromLocalInput(recordedAt) };
      if (entry) await api.put(`/clients/${clientId}/charts/${chart.id}/entries/${entry.id}`, body);
      else await api.post(`/clients/${clientId}/charts/${chart.id}/entries`, body);
      toast.success(entry ? 'Entry updated' : 'Entry recorded');
      onSaved(again);
      if (again) {
        const d = {};
        (chart.fields || []).forEach(f => { if (f.type === 'time' && AUTO_TIME_KEYS.has(f.key)) d[f.key] = nowHHMM(); });
        setFormData(d); setNotes(''); setRecordedAt(toLocalInput(Date.now()));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save the entry');
    } finally { setSaving(false); }
  }

  const pairedSet = new Set((cfg.pairedFields || []).flat());
  const boolGroupSet = new Set(cfg.boolGroup || []);
  const boolGroupFields = (chart.fields || []).filter(f => boolGroupSet.has(f.key));

  function renderField(field) {
    if (cfg.conditionals?.[field.key] && !cfg.conditionals[field.key](formData)) return null;
    if (pairedSet.has(field.key) || boolGroupSet.has(field.key)) return null;
    const isReadonly = cfg.autoCalc && field.key in cfg.autoCalc;
    if (cfg.prominentFields?.has(field.key) && field.type === 'boolean') {
      const active = !!formData[field.key];
      return (
        <div key={field.key} className={`rounded-xl border-2 p-4 ${active ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white'}`}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-red-700">{field.label}</p>
              <p className="text-xs text-red-500">If yes, follow the mealtime plan and report it.</p>
            </div>
            <button type="button" onClick={() => setField(field.key, !active)}
              className={`px-4 py-2 rounded-xl border-2 font-bold text-sm ${active ? 'bg-red-500 border-red-500 text-white' : 'border-slate-200 text-slate-600'}`}>
              {active ? 'Yes' : 'No'}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div key={field.key}>
        <label className="flex items-center gap-1 text-xs font-semibold mb-1.5 text-slate-600">
          {field.label}
          {field.required && !isReadonly && <span className="text-red-500">*</span>}
          {isReadonly && <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1 ml-1">Worked out</span>}
        </label>
        {isReadonly
          ? <div className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-600 bg-slate-50">{formData[field.key] ?? '—'}</div>
          : <DynamicField field={field} value={formData[field.key]} onChange={v => setField(field.key, v)} isCounter={cfg.counterFields?.has(field.key)} />}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label={`${entry ? 'Edit' : 'New'} ${chart.name} entry`}>
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden animate-slide-left">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50">
          <div>
            <h3 className="font-bold text-slate-900">{entry ? 'Edit entry' : 'New entry'}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{chart.name}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-200"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {cfg.banner && (
            <div className="flex gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertTriangle size={15} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs font-semibold text-red-700">{cfg.banner}</p>
            </div>
          )}
          {chart.instructions && (
            <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800"><span className="font-bold">Instructions: </span>{chart.instructions}</div>
          )}
          <div>
            <label className="flex items-center gap-1 text-xs font-semibold text-slate-600 mb-1.5"><Clock size={12} /> Date and time</label>
            <div className="flex gap-2">
              <input type="datetime-local" value={recordedAt} onChange={e => setRecordedAt(e.target.value)}
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
              <button type="button" onClick={() => setRecordedAt(toLocalInput(Date.now()))} className="px-3 text-xs font-semibold text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100">Now</button>
            </div>
          </div>

          {boolGroupFields.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Care given</p>
              <div className="grid grid-cols-2 gap-2">
                {boolGroupFields.map(field => {
                  const v = !!formData[field.key];
                  return (
                    <button key={field.key} type="button" onClick={() => setField(field.key, !v)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-xs font-semibold ${v ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-200 text-slate-600 bg-white hover:border-slate-300'}`}>
                      {v ? <Check size={13} /> : <span className="w-3.5 h-3.5 rounded-full border-2 border-slate-300" />}{field.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(cfg.pairedFields || []).map(([a, b]) => {
            const ra = getRangeStatus(a, formData[a]); const rb = getRangeStatus(b, formData[b]);
            return (
              <div key={a + b}>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Blood pressure (mmHg)</label>
                <div className="flex items-center gap-2">
                  <input type="number" inputMode="decimal" value={formData[a] ?? ''} onChange={e => setField(a, e.target.value === '' ? '' : Number(e.target.value))} placeholder="Systolic"
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
                  <span className="text-slate-400 font-bold text-lg">/</span>
                  <input type="number" inputMode="decimal" value={formData[b] ?? ''} onChange={e => setField(b, e.target.value === '' ? '' : Number(e.target.value))} placeholder="Diastolic"
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
                </div>
                {(ra || rb) && <p className="text-xs mt-1 flex gap-3">{ra && <span className={ra.cls}>Systolic: {ra.label}</span>}{rb && <span className={rb.cls}>Diastolic: {rb.label}</span>}</p>}
              </div>
            );
          })}

          {(chart.fields || []).map(renderField)}

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Anything else worth recording"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none" />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex gap-2">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          {!entry && <button onClick={() => submit(true)} disabled={saving} className="btn-secondary flex-1">Save and add another</button>}
          <button onClick={() => submit(false)} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}{saving ? 'Saving…' : entry ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Quick record: the common fields, right on the card ────────────────────────
function QuickRecord({ chart, onSaved, onMore }) {
  const { id: clientId } = useParams();
  const cfg = CHART_CONFIG[chart.slug] || {};
  const keys = QUICK_FIELDS[chart.slug] || [];
  const fields = keys.map(k => (chart.fields || []).find(f => f.key === k)).filter(Boolean);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setData(prev => {
    const next = { ...prev, [k]: v };
    if (cfg.autoCalc) for (const [ck, fn] of Object.entries(cfg.autoCalc)) { const r = fn(next); if (r !== null) next[ck] = r; }
    return next;
  });

  async function save() {
    const full = { ...data };
    (chart.fields || []).forEach(f => { if (f.type === 'time' && AUTO_TIME_KEYS.has(f.key) && !full[f.key]) full[f.key] = nowHHMM(); });
    const missing = missingRequired(chart, full);
    if (missing.length) { toast.error(`Still needed: ${missing.map(f => f.label).join(', ')}`); return; }
    if (!Object.keys(data).length) { toast.error('Fill in at least one field'); return; }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/charts/${chart.id}/entries`, { data: full, recorded_at: new Date().toISOString() });
      toast.success('Recorded');
      setData({});
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save');
    } finally { setSaving(false); }
  }

  if (!fields.length) return null;
  return (
    <div className="rounded-xl border border-primary-100 bg-primary-50/40 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold text-primary-700 flex items-center gap-1.5"><Zap size={13} /> Quick record <span className="font-medium text-primary-600/80">· now, {clockOf(Date.now())}</span></p>
        <button onClick={() => onMore(data)} className="text-xs font-semibold text-primary-700 hover:underline">All fields</button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map(f => {
          if (cfg.conditionals?.[f.key] && !cfg.conditionals[f.key](data)) return null;
          const wide = (f.type === 'select' && (f.options || []).length > 4) || f.type === 'scale';
          return (
            <div key={f.key} className={wide ? 'sm:col-span-2' : ''}>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
              <DynamicField field={f} value={data[f.key]} onChange={v => setField(f.key, v)} isCounter={cfg.counterFields?.has(f.key)} compact />
            </div>
          );
        })}
      </div>
      <div className="flex justify-end mt-3">
        <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2 text-sm">
          {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />} Save entry
        </button>
      </div>
    </div>
  );
}

// ── Trend: a small line per numeric field ─────────────────────────────────────
function Sparkline({ values, color = '#6366f1' }) {
  if (values.length < 2) return null;
  const w = 120; const h = 32;
  const min = Math.min(...values); const max = Math.max(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - 3 - ((v - min) / span) * (h - 6)}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={w} cy={h - 3 - ((values[values.length - 1] - min) / span) * (h - 6)} r="2.5" fill={color} />
    </svg>
  );
}
function Trends({ chart, entries }) {
  const fields = (chart.fields || []).filter(f => f.type === 'number' || f.type === 'scale');
  const cards = fields.map(f => {
    const clean = entries.slice().reverse().map(e => e.data?.[f.key]).filter(v => v !== undefined && v !== null && v !== '').map(Number).filter(v => !Number.isNaN(v));
    if (!clean.length) return null;
    const last = clean[clean.length - 1];
    const avg = clean.reduce((a, b) => a + b, 0) / clean.length;
    const r = getRangeStatus(f.key, last);
    return (
      <div key={f.key} className="bg-white rounded-xl border border-slate-100 p-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide truncate">{f.label}</p>
          <p className={`text-lg font-bold ${r?.cls || 'text-slate-800'}`}>{last}</p>
          <p className="text-[10px] text-slate-400">avg {avg.toFixed(1)} · {clean.length} readings</p>
        </div>
        <Sparkline values={clean.slice(-20)} color={r && r.cell ? '#dc2626' : '#6366f1'} />
      </div>
    );
  }).filter(Boolean).slice(0, 6);
  if (!cards.length) return null;
  return <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">{cards}</div>;
}

// ── Chart settings (frequency + instructions) ─────────────────────────────────
function ChartSettingsModal({ chart, onClose, onSaved }) {
  const { id: clientId } = useParams();
  const [frequency, setFrequency] = useState(chart.frequency || '');
  const [instructions, setInstructions] = useState(chart.instructions || '');
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/charts/${chart.id}`, { frequency, instructions });
      toast.success('Chart settings saved');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Could not save'); } finally { setSaving(false); }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={`${chart.name} settings`}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div><h3 className="font-bold text-slate-900">{chart.name}</h3><p className="text-xs text-slate-500">How often, and what staff should know</p></div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">How often</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {FREQ_PRESETS.map(p => (
            <button key={p} type="button" onClick={() => setFrequency(p)}
              className={`px-3 py-1 rounded-xl text-xs font-semibold border-2 ${frequency === p ? 'bg-primary-500 border-primary-500 text-white' : 'border-slate-200 text-slate-600 hover:border-primary-300'}`}>{p}</button>
          ))}
        </div>
        <input value={frequency} onChange={e => setFrequency(e.target.value)} placeholder="Or type, e.g. Every 3 hours"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mb-1 focus:outline-none focus:ring-2 focus:ring-primary-400" />
        <p className="text-[11px] text-slate-400 mb-4">An interval such as "Every 4 hours" or "Daily" shows when the next entry is due.</p>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Instructions for staff</label>
        <textarea value={instructions} onChange={e => setInstructions(e.target.value)} rows={3} placeholder="e.g. Check blood glucose before meals; call the nurse if under 4"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400" />
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary flex-1">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Medication chart
//
// A medication is { id, name, dose, route, frequency, times: ['08:00', …], prn, prn_indication,
// prn_max_24h, prn_min_hours, prescriber, instructions }. Entries come in two kinds:
//   round  { kind:'round', round_time, staff_initials, medications: { [id]: { given, status, reason_not_given, time } } }
//   prn    { kind:'prn', staff_initials, prn_reason, prn_effect, prn_effect_at, medications: { [id]: { given:true, status:'given', prn:true, dose, time } } }
// `given` stays on every medication record so older entries and the carer app keep counting the
// same way. An entry without a kind is an older round.
// ═════════════════════════════════════════════════════════════════════════════
const ROUTES = ['Oral', 'Sublingual', 'Topical', 'Inhaled', 'Injection', 'Eye drops', 'Ear drops', 'Patch', 'PR', 'PEG', 'Other'];
const TIME_PRESETS = [['Morning', '08:00'], ['Midday', '12:00'], ['Afternoon', '17:00'], ['Night', '20:00']];
const NOT_GIVEN = [
  ['refused', 'Refused'], ['withheld', 'Withheld'], ['not_available', 'Not available'],
  ['asleep', 'Asleep'], ['away', 'Away or in hospital'], ['other', 'Other'],
];
const STATUS_LABEL = Object.fromEntries([['given', 'Given'], ...NOT_GIVEN]);
const PRN_EFFECT = ['Effective', 'Partly effective', 'Not effective'];

const medKind = (e) => e?.data?.kind || 'round';
const timeLabel = (t) => {
  if (!t) return 'Any time';
  const [h, m] = t.split(':').map(Number);
  const d = new Date(); d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
};
const slotMs = (t, base = Date.now()) => { const [h, m] = t.split(':').map(Number); const d = new Date(base); d.setHours(h, m, 0, 0); return d.getTime(); };

function MedForm({ med, onClose, onSave }) {
  const [m, setM] = useState(() => ({ name: '', dose: '', route: 'Oral', frequency: '', times: [], prn: false, prn_indication: '', prn_max_24h: '', prn_min_hours: '', prescriber: '', instructions: '', ...(med || {}) }));
  const [newTime, setNewTime] = useState('');
  const set = (k, v) => setM(p => ({ ...p, [k]: v }));
  const addTime = (t) => { if (!/^\d{2}:\d{2}$/.test(t)) return; set('times', [...new Set([...(m.times || []), t])].sort()); setNewTime(''); };
  function save() {
    if (!m.name.trim()) { toast.error('Add the medication name'); return; }
    if (!m.prn && !(m.times || []).length) { toast.error('Add at least one time, or mark it as PRN'); return; }
    onSave({ ...m, id: m.id || (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())), times: m.prn ? [] : m.times });
  }
  const input = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={med ? 'Edit medication' : 'Add medication'}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Pill size={16} className="text-purple-600" /> {med ? 'Edit medication' : 'Add medication'}</h3>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="block text-xs font-semibold text-slate-600 mb-1">Medication *</label><input className={input} value={m.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Paracetamol 500 mg" autoFocus /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Dose</label><input className={input} value={m.dose} onChange={e => set('dose', e.target.value)} placeholder="e.g. 2 tablets" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Route</label>
              <select className={input} value={m.route} onChange={e => set('route', e.target.value)}>{ROUTES.map(r => <option key={r}>{r}</option>)}</select></div>
          </div>

          <div className="flex gap-2" role="radiogroup" aria-label="Medication type">
            {[[false, 'Regular (scheduled)'], [true, 'PRN (as needed)']].map(([v, l]) => (
              <button key={l} type="button" role="radio" aria-checked={m.prn === v} onClick={() => set('prn', v)}
                className={`flex-1 py-2 rounded-xl border-2 text-sm font-semibold ${m.prn === v ? (v ? 'bg-amber-500 border-amber-500 text-white' : 'bg-purple-600 border-purple-600 text-white') : 'border-slate-200 text-slate-600'}`}>{l}</button>
            ))}
          </div>

          {!m.prn ? (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Times given each day *</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {TIME_PRESETS.map(([l, t]) => (
                  <button key={t} type="button" onClick={() => ((m.times || []).includes(t) ? set('times', m.times.filter(x => x !== t)) : addTime(t))}
                    className={`px-3 py-1 rounded-xl text-xs font-semibold border-2 ${(m.times || []).includes(t) ? 'bg-purple-600 border-purple-600 text-white' : 'border-slate-200 text-slate-600 hover:border-purple-300'}`}>{l} {timeLabel(t)}</button>
                ))}
              </div>
              <div className="flex gap-2 items-center">
                <input type="time" value={newTime} onChange={e => setNewTime(e.target.value)} className={`${input} w-36`} />
                <button type="button" onClick={() => addTime(newTime)} className="text-xs font-semibold text-purple-700 bg-purple-50 px-3 py-2 rounded-lg hover:bg-purple-100">Add time</button>
              </div>
              {(m.times || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {m.times.map(t => (
                    <span key={t} className="inline-flex items-center gap-1 text-xs font-semibold bg-purple-100 text-purple-800 rounded-full px-2.5 py-1">
                      {timeLabel(t)}<button type="button" aria-label={`Remove ${timeLabel(t)}`} onClick={() => set('times', m.times.filter(x => x !== t))}><X size={11} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 rounded-xl bg-amber-50 border border-amber-100 p-3">
              <div className="col-span-2"><label className="block text-xs font-semibold text-slate-600 mb-1">Give for (indication)</label><input className={input} value={m.prn_indication} onChange={e => set('prn_indication', e.target.value)} placeholder="e.g. Pain above 4/10, or anxiety" /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Most doses in 24 h</label><input type="number" min="1" className={input} value={m.prn_max_24h} onChange={e => set('prn_max_24h', e.target.value)} placeholder="e.g. 4" /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">At least hours apart</label><input type="number" min="0" step="0.5" className={input} value={m.prn_min_hours} onChange={e => set('prn_min_hours', e.target.value)} placeholder="e.g. 4" /></div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Frequency (as written)</label><input className={input} value={m.frequency} onChange={e => set('frequency', e.target.value)} placeholder="e.g. Twice daily" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Prescriber</label><input className={input} value={m.prescriber} onChange={e => set('prescriber', e.target.value)} placeholder="e.g. Dr Patel" /></div>
            <div className="col-span-2"><label className="block text-xs font-semibold text-slate-600 mb-1">Instructions</label><input className={input} value={m.instructions} onChange={e => set('instructions', e.target.value)} placeholder="e.g. With food; crush and mix with yoghurt" /></div>
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button onClick={save} className="btn-primary flex-1">{med ? 'Save changes' : 'Add medication'}</button>
        </div>
      </div>
    </div>
  );
}

// Record a dose that wasn't simply "given": choose why, in one click.
function NotGivenPicker({ onPick, onClose }) {
  const [other, setOther] = useState('');
  return (
    <div className="absolute z-20 mt-1 right-0 w-56 bg-white rounded-xl shadow-xl border border-slate-200 p-2" onClick={e => e.stopPropagation()}>
      <p className="text-[11px] font-semibold text-slate-500 px-1 pb-1">Not given because…</p>
      {NOT_GIVEN.filter(([k]) => k !== 'other').map(([k, l]) => (
        <button key={k} onClick={() => onPick(k, l)} className="w-full text-left text-sm px-2 py-1.5 rounded-lg hover:bg-amber-50 text-slate-700">{l}</button>
      ))}
      <div className="flex gap-1 mt-1">
        <input value={other} onChange={e => setOther(e.target.value)} placeholder="Other reason" className="flex-1 border border-slate-200 rounded-lg px-2 py-1 text-xs" />
        <button disabled={!other.trim()} onClick={() => onPick('other', other.trim())} className="text-xs font-semibold text-white bg-amber-500 rounded-lg px-2 disabled:opacity-40">OK</button>
      </div>
      <button onClick={onClose} className="w-full text-xs text-slate-400 mt-1 py-1 hover:text-slate-600">Cancel</button>
    </div>
  );
}

function PrnModal({ med, recentCount, lastMs, initials, onClose, onSave }) {
  const [reason, setReason] = useState('');
  const [dose, setDose] = useState(med.dose || '');
  const [time, setTime] = useState(nowHHMM());
  const [notes, setNotes] = useState('');
  const max = Number(med.prn_max_24h) || 0;
  const gap = Number(med.prn_min_hours) || 0;
  const tooMany = max && recentCount >= max;
  const tooSoon = gap && lastMs && Date.now() - lastMs < gap * 3600000;
  const input = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={`Give ${med.name}`}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[11px] font-bold text-amber-700 uppercase tracking-wide">PRN dose</p>
            <h3 className="font-bold text-slate-900">{med.name}</h3>
            {med.prn_indication && <p className="text-xs text-slate-500 mt-0.5">Give for: {med.prn_indication}</p>}
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        {(tooMany || tooSoon) && (
          <div className="flex gap-2 p-3 mb-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-semibold">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>{tooMany ? `${recentCount} of ${max} doses already given in the last 24 hours.` : ''} {tooSoon ? `Last given ${ago(lastMs)}; it should be at least ${gap} hours apart.` : ''} Check with the nurse or prescriber before giving.</span>
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Reason given *</label>
            {med.prn_indication && (
              <button type="button" onClick={() => setReason(med.prn_indication)} className="mb-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100">{med.prn_indication}</button>
            )}
            <input className={input} value={reason} onChange={e => setReason(e.target.value)} placeholder="What was observed" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Dose</label><input className={input} value={dose} onChange={e => setDose(e.target.value)} /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Time</label><input type="time" className={input} value={time} onChange={e => setTime(e.target.value)} /></div>
          </div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Notes</label><input className={input} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" /></div>
          <p className="text-[11px] text-slate-400">Recorded by {initials || 'you'}. You'll be asked how well it worked later.</p>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button disabled={!reason.trim()} onClick={() => onSave({ reason: reason.trim(), dose, time, notes })}
            className="flex-1 py-2 rounded-xl font-semibold text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-40">Record PRN dose</button>
        </div>
      </div>
    </div>
  );
}

function MedicationChart({ chart, onChanged }) {
  const { id: clientId } = useParams();
  const { user } = useAuth();
  const confirm = useConfirm();
  const myInitials = initialsOf(user);
  const [meds, setMeds] = useState(chart.metadata?.medications || []);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null); // `${medId}|${time}`
  const [busy, setBusy] = useState(null);
  const [medForm, setMedForm] = useState(null); // {} for new, med for edit
  const [prnFor, setPrnFor] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/clients/${clientId}/charts/${chart.id}/entries`); setEntries(data); }
    catch { toast.error('Could not load medication records'); } finally { setLoading(false); }
  }, [clientId, chart.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const close = () => setPicker(null); window.addEventListener('click', close); return () => window.removeEventListener('click', close); }, []);

  const regular = meds.filter(m => !m.prn);
  const prn = meds.filter(m => m.prn);
  const now = Date.now();
  const todays = entries.filter(e => sameDay(entryMs(e), now));
  const slots = [...new Set(regular.flatMap(m => (m.times && m.times.length ? m.times : [''])))].sort();

  // Today's round entry for a time slot (one per slot keeps the record tidy).
  // The app may save a second entry for the same round, so a dose is read from the latest entry
  // for that slot that mentions it.
  const roundFor = (t) => todays.find(e => medKind(e) === 'round' && (e.data?.round_time || '') === t);
  const cellOf = (medId, t) => todays.find(e => medKind(e) === 'round' && (e.data?.round_time || '') === t && e.data?.medications?.[medId])?.data?.medications?.[medId] || null;

  async function persistMeds(next, msg = 'Medications saved') {
    try {
      await api.put(`/clients/${clientId}/charts/${chart.id}/medications`, { medications: next });
      setMeds(next); toast.success(msg); onChanged?.();
    } catch { toast.error('Could not save the medication list'); }
  }

  async function recordDose(med, t, status, reason) {
    const key = `${med.id}|${t}`;
    setBusy(key); setPicker(null);
    const rec = { given: status === 'given', status, reason_not_given: status === 'given' ? '' : reason, time: nowHHMM(), recorded_by: myInitials };
    try {
      const existing = roundFor(t);
      if (existing) {
        const data = { ...existing.data, medications: { ...(existing.data?.medications || {}), [med.id]: rec } };
        await api.put(`/clients/${clientId}/charts/${chart.id}/entries/${existing.id}`, { data });
      } else {
        await api.post(`/clients/${clientId}/charts/${chart.id}/entries`, {
          data: { kind: 'round', round_time: t, staff_initials: myInitials, medications: { [med.id]: rec } },
          recorded_at: new Date(t ? slotMs(t) : now).toISOString(),
        });
      }
      toast.success(status === 'given' ? `${med.name}: given` : `${med.name}: ${STATUS_LABEL[status] || reason}`);
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.message || 'Could not record the dose'); } finally { setBusy(null); }
  }

  async function giveRound(t) {
    const due = regular.filter(m => (m.times && m.times.length ? m.times.includes(t) : t === '') && !cellOf(m.id, t));
    if (!due.length) return;
    const ok = await confirm(`Record ${due.map(m => m.name).join(', ')} as given at ${timeLabel(t)}?`, { title: `Mark ${due.length} as given`, confirmLabel: `Mark ${due.length} given` });
    if (!ok) return;
    setBusy(`round|${t}`);
    try {
      const existing = roundFor(t);
      const add = Object.fromEntries(due.map(m => [m.id, { given: true, status: 'given', reason_not_given: '', time: nowHHMM(), recorded_by: myInitials }]));
      if (existing) await api.put(`/clients/${clientId}/charts/${chart.id}/entries/${existing.id}`, { data: { ...existing.data, medications: { ...(existing.data?.medications || {}), ...add } } });
      else await api.post(`/clients/${clientId}/charts/${chart.id}/entries`, { data: { kind: 'round', round_time: t, staff_initials: myInitials, medications: add }, recorded_at: new Date(t ? slotMs(t) : now).toISOString() });
      toast.success(`${due.length} recorded as given`);
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.message || 'Could not record the round'); } finally { setBusy(null); }
  }

  async function savePrn(med, { reason, dose, time, notes }) {
    try {
      const [h, m] = time.split(':').map(Number); const d = new Date(); d.setHours(h, m, 0, 0);
      await api.post(`/clients/${clientId}/charts/${chart.id}/entries`, {
        data: { kind: 'prn', staff_initials: myInitials, prn_reason: reason, medications: { [med.id]: { given: true, status: 'given', prn: true, dose, time, recorded_by: myInitials } } },
        notes: notes || null, recorded_at: d.toISOString(),
      });
      toast.success(`${med.name} PRN dose recorded`);
      setPrnFor(null); await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.message || 'Could not record the PRN dose'); }
  }

  async function recordEffect(entry, effect) {
    try {
      await api.put(`/clients/${clientId}/charts/${chart.id}/entries/${entry.id}`, { data: { ...entry.data, prn_effect: effect, prn_effect_at: new Date().toISOString() } });
      toast.success('Effect recorded'); await load();
    } catch { toast.error('Could not record the effect'); }
  }

  async function deleteEntry(e) {
    const ok = await confirm('This medication record will be deleted for good.', { title: 'Delete this record?', danger: true, confirmLabel: 'Delete record' });
    if (!ok) return;
    try { await api.delete(`/clients/${clientId}/charts/${chart.id}/entries/${e.id}`); toast.success('Record deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not delete'); }
  }

  async function analyzePDF(file) {
    setAnalyzing(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post(`/clients/${clientId}/charts/${chart.id}/analyze-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const found = (data.medications || []).filter(x => !meds.find(e => e.name.toLowerCase() === x.name.toLowerCase()));
      await persistMeds([...meds, ...found], `${found.length} medication${found.length === 1 ? '' : 's'} added from the document. Check each one.`);
    } catch (err) { toast.error(err.response?.data?.message || 'Could not read the document'); }
    finally { setAnalyzing(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  const prnEntries = entries.filter(e => medKind(e) === 'prn');
  const prnStats = (medId) => {
    const mine = prnEntries.filter(e => e.data?.medications?.[medId]);
    const last = mine[0] ? entryMs(mine[0]) : null;
    return { last, count24: mine.filter(e => now - entryMs(e) < 86400000).length };
  };
  const awaitingEffect = prnEntries.filter(e => !e.data?.prn_effect && now - entryMs(e) > 20 * 60000 && now - entryMs(e) < 48 * 3600000);
  const medName = (id) => meds.find(m => m.id === id)?.name || 'Removed medication';

  function exportMar() {
    const rows = [];
    entries.forEach(e => {
      Object.entries(e.data?.medications || {}).forEach(([id, r]) => rows.push([
        fmtDT(entryMs(e)), medKind(e) === 'prn' ? 'PRN' : `Round ${timeLabel(e.data?.round_time || '')}`, medName(id), r.dose || meds.find(m => m.id === id)?.dose || '',
        STATUS_LABEL[r.status] || (r.given ? 'Given' : 'Not given'), r.reason_not_given || e.data?.prn_reason || '', r.time || '', r.recorded_by || e.data?.staff_initials || '', e.recorded_by_name || '', e.data?.prn_effect || '',
      ]));
    });
    if (!rows.length) { toast('Nothing to export yet'); return; }
    downloadCSV(`${chart.name}`, ['Recorded', 'Type', 'Medication', 'Dose', 'Outcome', 'Reason', 'Time', 'Initials', 'Staff', 'PRN effect'], rows);
  }

  if (loading) return <div className="flex items-center justify-center py-10 gap-2 text-slate-400"><RefreshCw size={16} className="animate-spin" /> Loading medications…</div>;

  return (
    <div className="space-y-5">
      {/* Today's MAR */}
      <section>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h5 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Pill size={15} className="text-purple-600" /> Today's medications <span className="text-xs font-medium text-slate-400">{new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' })}</span></h5>
          <div className="flex items-center gap-2">
            <button onClick={exportMar} className="text-xs font-semibold text-slate-500 hover:text-primary-600 flex items-center gap-1"><Download size={12} /> Export</button>
            <button onClick={() => setShowHistory(s => !s)} className="text-xs font-semibold text-slate-500 hover:text-primary-600 flex items-center gap-1"><History size={12} /> History</button>
          </div>
        </div>
        {regular.length === 0 ? (
          <p className="text-sm text-slate-400 bg-slate-50 rounded-xl px-4 py-6 text-center">No regular medications yet. Add them below and they'll appear here for each round.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-purple-50/60">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Medication</th>
                  {slots.map(t => {
                    const pending = regular.some(m => (m.times?.length ? m.times.includes(t) : t === '') && !cellOf(m.id, t));
                    const late = t && pending && now > slotMs(t) + 60 * 60000;
                    return (
                      <th key={t || 'any'} className="px-2 py-2 text-center min-w-[110px]">
                        <div className={`text-xs font-bold ${late ? 'text-red-600' : 'text-slate-700'}`}>{timeLabel(t)}</div>
                        {pending && (
                          <button onClick={() => giveRound(t)} disabled={busy === `round|${t}`}
                            className="mt-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 hover:bg-emerald-100 disabled:opacity-50">All given</button>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {regular.map(m => (
                  <tr key={m.id}>
                    <td className="px-3 py-2.5">
                      <p className="font-semibold text-slate-800">{m.name}</p>
                      <p className="text-[11px] text-slate-500">{[m.dose, m.route, m.instructions].filter(Boolean).join(' · ')}</p>
                    </td>
                    {slots.map(t => {
                      const scheduled = m.times?.length ? m.times.includes(t) : t === '';
                      if (!scheduled) return <td key={t || 'any'} className="px-2 py-2 text-center text-slate-200">—</td>;
                      const c = cellOf(m.id, t);
                      const key = `${m.id}|${t}`;
                      const late = t && !c && now > slotMs(t) + 60 * 60000;
                      if (c) {
                        const given = c.given || c.status === 'given';
                        return (
                          <td key={t || 'any'} className="px-2 py-2 text-center">
                            <span title={c.reason_not_given || ''} className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-1 ${given ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>
                              {given ? <Check size={12} /> : <X size={12} />}{given ? 'Given' : (STATUS_LABEL[c.status] || 'Not given')}
                            </span>
                            <p className="text-[10px] text-slate-400 mt-0.5">{c.time} {c.recorded_by || ''}</p>
                          </td>
                        );
                      }
                      return (
                        <td key={t || 'any'} className="px-2 py-2 text-center relative">
                          <div className="inline-flex rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                            <button onClick={e => { e.stopPropagation(); recordDose(m, t, 'given', ''); }} disabled={busy === key} title="Given"
                              className={`px-2.5 py-1 text-xs font-bold ${late ? 'bg-red-50 text-red-700' : 'bg-white text-emerald-700'} hover:bg-emerald-500 hover:text-white disabled:opacity-50`}>
                              {busy === key ? <RefreshCw size={12} className="animate-spin" /> : <span className="flex items-center gap-1"><Check size={12} /> Give</span>}
                            </button>
                            <button onClick={e => { e.stopPropagation(); setPicker(picker === key ? null : key); }} title="Not given" aria-label={`${m.name} not given`}
                              className="px-1.5 border-l border-slate-200 bg-white text-slate-400 hover:bg-amber-50 hover:text-amber-700"><X size={12} /></button>
                          </div>
                          {late && <p className="text-[10px] text-red-600 font-semibold mt-0.5">Late</p>}
                          {picker === key && <NotGivenPicker onClose={() => setPicker(null)} onPick={(k, l) => recordDose(m, t, k, l)} />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* PRN */}
      <section>
        <h5 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><Zap size={15} className="text-amber-500" /> PRN (as needed)</h5>
        {awaitingEffect.length > 0 && (
          <div className="mb-3 space-y-2">
            {awaitingEffect.map(e => {
              const id = Object.keys(e.data?.medications || {})[0];
              return (
                <div key={e.id} className="flex flex-wrap items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2">
                  <span className="text-xs text-amber-900 flex-1 min-w-[180px]"><span className="font-bold">{medName(id)}</span> given {ago(entryMs(e))} for "{e.data?.prn_reason}". How well did it work?</span>
                  {PRN_EFFECT.map(x => <button key={x} onClick={() => recordEffect(e, x)} className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-white border border-amber-200 hover:bg-amber-100">{x}</button>)}
                </div>
              );
            })}
          </div>
        )}
        {prn.length === 0 ? (
          <p className="text-sm text-slate-400 bg-slate-50 rounded-xl px-4 py-4 text-center">No PRN medications. Add one below and mark it PRN.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {prn.map(m => {
              const st = prnStats(m.id);
              const max = Number(m.prn_max_24h) || 0;
              return (
                <div key={m.id} className="rounded-xl border border-amber-200 bg-white p-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm">{m.name} <span className="text-[10px] font-bold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 ml-1">PRN</span></p>
                    <p className="text-[11px] text-slate-500">{[m.dose, m.route, m.prn_indication && `for ${m.prn_indication}`].filter(Boolean).join(' · ')}</p>
                    <p className="text-[11px] mt-1 text-slate-500">
                      {st.last ? `Last given ${ago(st.last)}` : 'Not given yet'}{max ? ` · ${st.count24} of ${max} in 24 h` : st.count24 ? ` · ${st.count24} in 24 h` : ''}
                    </p>
                  </div>
                  <button onClick={() => setPrnFor(m)} className="shrink-0 text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 rounded-lg px-3 py-1.5">Give</button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* History */}
      {showHistory && (
        <section>
          <h5 className="text-sm font-bold text-slate-800 mb-2">Recent records</h5>
          {entries.length === 0 ? <p className="text-sm text-slate-400">Nothing recorded yet.</p> : (
            <div className="overflow-x-auto rounded-xl border border-slate-100">
              <table className="min-w-full text-xs divide-y divide-slate-100">
                <thead className="bg-slate-50"><tr>
                  {['When', 'Type', 'Outcome', 'Staff', ''].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wide">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-slate-50 bg-white">
                  {entries.slice(0, 40).map(e => {
                    const list = Object.entries(e.data?.medications || {});
                    const notGiven = list.filter(([, r]) => !(r.given || r.status === 'given'));
                    return (
                      <tr key={e.id}>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDT(entryMs(e))}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{medKind(e) === 'prn'
                          ? <span className="font-bold text-amber-700">PRN · {medName(list[0]?.[0])}</span>
                          : <span className="font-semibold text-purple-700">Round {timeLabel(e.data?.round_time || '')}</span>}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {medKind(e) === 'prn'
                            ? <>{e.data?.prn_reason}{e.data?.prn_effect && <span className="ml-1 text-slate-400">· {e.data.prn_effect}</span>}</>
                            : <>{list.length - notGiven.length}/{list.length} given{notGiven.length > 0 && <span className="text-amber-700"> · {notGiven.map(([id, r]) => `${medName(id)} ${(STATUS_LABEL[r.status] || 'not given').toLowerCase()}${r.reason_not_given && r.status === 'other' ? ` (${r.reason_not_given})` : ''}`).join(', ')}</span>}</>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-500">{e.data?.staff_initials || '—'} {e.recorded_by_name ? `· ${e.recorded_by_name}` : ''}</td>
                        <td className="px-3 py-2 text-right"><button onClick={() => deleteEntry(e)} aria-label="Delete record" className="p-1 text-slate-300 hover:text-red-500"><Trash2 size={13} /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Medication list */}
      <section className="rounded-xl border border-purple-100 bg-purple-50/40 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h5 className="text-xs font-bold text-purple-800 uppercase tracking-wide">Medication list ({meds.length})</h5>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" className="hidden" accept="application/pdf,image/*" onChange={e => e.target.files[0] && analyzePDF(e.target.files[0])} />
            <button onClick={() => fileRef.current?.click()} disabled={analyzing}
              className="flex items-center gap-1.5 text-xs font-semibold text-purple-700 bg-white border border-purple-200 hover:bg-purple-50 px-2.5 py-1.5 rounded-lg disabled:opacity-50">
              {analyzing ? <RefreshCw size={11} className="animate-spin" /> : <Upload size={11} />}{analyzing ? 'Reading…' : 'Read from a medication chart (PDF)'}
            </button>
            <button onClick={() => setMedForm({})} className="flex items-center gap-1.5 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 px-2.5 py-1.5 rounded-lg"><Plus size={11} /> Add medication</button>
          </div>
        </div>
        {meds.length === 0 ? <p className="text-xs text-slate-500">No medications yet. Add them, or read them from a pharmacy medication chart.</p> : (
          <div className="grid gap-2 sm:grid-cols-2">
            {meds.map(m => (
              <div key={m.id} className="flex items-start gap-2 bg-white border border-purple-100 rounded-xl px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{m.name}{m.prn && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 ml-1.5">PRN</span>}</p>
                  <p className="text-[11px] text-slate-500">{[m.dose, m.route, m.prn ? (m.prn_indication && `for ${m.prn_indication}`) : (m.times || []).map(timeLabel).join(', '), m.prescriber].filter(Boolean).join(' · ')}</p>
                  {!m.prn && !(m.times || []).length && <p className="text-[11px] text-amber-700 font-semibold">No set times yet, so it shows under "Any time". Edit to add times.</p>}
                </div>
                <button onClick={() => setMedForm(m)} aria-label={`Edit ${m.name}`} className="p-1 text-slate-400 hover:text-purple-700"><Edit2 size={13} /></button>
                <button aria-label={`Remove ${m.name}`} className="p-1 text-slate-300 hover:text-red-500" onClick={async () => {
                  if (await confirm(`${m.name} will come off this client's medication list. Past records stay.`, { title: 'Remove medication?', danger: true, confirmLabel: 'Remove' })) persistMeds(meds.filter(x => x.id !== m.id), 'Medication removed');
                }}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </section>

      {medForm && <MedForm med={medForm.id ? medForm : null} onClose={() => setMedForm(null)} onSave={m => { setMedForm(null); persistMeds(meds.some(x => x.id === m.id) ? meds.map(x => (x.id === m.id ? m : x)) : [...meds, m]); }} />}
      {prnFor && <PrnModal med={prnFor} recentCount={prnStats(prnFor.id).count24} lastMs={prnStats(prnFor.id).last} initials={myInitials} onClose={() => setPrnFor(null)} onSave={v => savePrn(prnFor, v)} />}
    </div>
  );
}

// ── One chart card ────────────────────────────────────────────────────────────
function ChartCard({ chart, open, onToggle, onRemove, onRefresh }) {
  const { id: clientId } = useParams();
  const confirm = useConfirm();
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [form, setForm] = useState(null); // { entry?, initial? }
  const [settings, setSettings] = useState(false);
  const meta = catMeta(chart.category);
  const isMed = chart.slug === 'medication_administration';
  const hasQuick = !!QUICK_FIELDS[chart.slug];
  const status = dueStatus(chart.frequency, Number(chart.last_entry_ms) || null);

  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try { const { data } = await api.get(`/clients/${clientId}/charts/${chart.id}/entries`); setEntries(data); }
    catch { toast.error('Could not load entries'); } finally { setLoadingEntries(false); }
  }, [clientId, chart.id]);
  useEffect(() => { if (open && !isMed) loadEntries(); }, [open, isMed, loadEntries]);

  async function deleteEntry(e) {
    const ok = await confirm('This entry will be deleted for good.', { title: 'Delete this entry?', danger: true, confirmLabel: 'Delete entry' });
    if (!ok) return;
    try { await api.delete(`/clients/${clientId}/charts/${chart.id}/entries/${e.id}`); toast.success('Entry deleted'); setEntries(p => p.filter(x => x.id !== e.id)); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not delete'); }
  }

  const tableFields = (chart.fields || []).filter(f => f.type !== 'textarea').slice(0, 5);
  const shown = showAll ? entries : entries.slice(0, 10);

  function fmtVal(field, val) {
    if (val === undefined || val === null || val === '') return <span className="text-slate-300">—</span>;
    if (typeof val === 'boolean') return val ? <span className="text-emerald-600 font-semibold">Yes</span> : <span className="text-slate-400">No</span>;
    if (field.type === 'scale') {
      const n = Number(val);
      const cls = field.key === 'mood_scale' ? (n <= 3 ? 'bg-red-100 text-red-700' : n <= 6 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700')
        : (n <= 3 ? 'bg-emerald-100 text-emerald-700' : n <= 6 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700');
      return <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${cls}`}>{n}</span>;
    }
    const r = getRangeStatus(field.key, val);
    return <span className={`px-1.5 py-0.5 rounded ${r?.cell || 'text-slate-700'}`}>{String(val)}</span>;
  }

  function primaryAction(e) {
    e.stopPropagation();
    if (isMed || hasQuick) { if (!open) onToggle(); return; }
    setForm({});
  }

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${status.overdue ? 'border-red-200' : status.due ? 'border-amber-200' : 'border-slate-100'}`}>
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3.5 cursor-pointer hover:bg-slate-50/70 select-none" onClick={onToggle}
        role="button" tabIndex={0} aria-expanded={open} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.badge}`}>
          {isMed ? <Pill size={18} aria-hidden /> : <meta.icon size={18} aria-hidden />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-slate-900 text-sm">{chart.name}</h4>
            {chart.frequency
              ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold inline-flex items-center gap-1"><Clock size={9} />{chart.frequency}</span>
              : !isMed && <button onClick={e => { e.stopPropagation(); setSettings(true); }} className="text-[10px] px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:text-primary-700 hover:border-primary-300">Set how often</button>}
            {isMed && (chart.metadata?.medications || []).length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold">{(chart.metadata.medications || []).length} medications</span>
            )}
          </div>
          <p className={`text-xs mt-0.5 ${TONE[status.tone]}`}>{status.text}{chart.entry_count ? ` · ${chart.entry_count} entr${chart.entry_count === 1 ? 'y' : 'ies'}` : ''}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={primaryAction}
            className={`flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-1.5 rounded-lg ${isMed ? 'bg-purple-600 hover:bg-purple-700' : 'bg-primary-500 hover:bg-primary-600'}`}>
            <Plus size={12} /> {isMed ? 'Medications' : 'Record'}
          </button>
          <button onClick={e => { e.stopPropagation(); setSettings(true); }} aria-label={`${chart.name} settings`} title="Settings" className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100"><Settings2 size={15} /></button>
          <button onClick={async e => {
            e.stopPropagation();
            if (await confirm(`${chart.name} and all its entries will be removed from this client.`, { title: 'Remove this chart?', danger: true, confirmLabel: 'Remove chart' })) onRemove();
          }} aria-label={`Remove ${chart.name}`} title="Remove chart" className="p-1.5 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50"><Trash2 size={15} /></button>
          {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-100 p-4 sm:p-5 space-y-4">
          {chart.instructions && (
            <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl flex gap-2 text-xs text-amber-800">
              <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" /><span><span className="font-bold">Instructions: </span>{chart.instructions}</span>
            </div>
          )}

          {isMed ? <MedicationChart chart={chart} onChanged={onRefresh} /> : (
            <>
              {hasQuick
                ? <QuickRecord chart={chart} onSaved={() => { loadEntries(); onRefresh(); }} onMore={initial => setForm({ initial })} />
                : (
                  <button onClick={() => setForm({})} className="w-full rounded-xl border-2 border-dashed border-primary-200 text-primary-700 font-semibold text-sm py-3 hover:bg-primary-50 flex items-center justify-center gap-2">
                    <Plus size={15} /> Record a new entry
                  </button>
                )}

              {loadingEntries ? (
                <div className="flex items-center justify-center py-8 gap-2 text-slate-400"><RefreshCw size={16} className="animate-spin" /> Loading entries…</div>
              ) : entries.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-4">No entries yet.</p>
              ) : (
                <>
                  <Trends chart={chart} entries={entries} />
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Entries ({entries.length})</p>
                    <button onClick={() => downloadCSV(chart.name, ['Recorded', 'Recorded by', ...(chart.fields || []).map(f => f.label), 'Notes'],
                      entries.map(e => [fmtDT(entryMs(e)), e.recorded_by_name || '', ...(chart.fields || []).map(f => { const v = e.data?.[f.key]; return typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v ?? ''; }), e.notes || '']))}
                      className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary-600 font-medium"><Download size={12} /> Export CSV</button>
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-slate-100">
                    <table className="min-w-full divide-y divide-slate-100 text-xs">
                      <thead className="bg-slate-50"><tr>
                        <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">When</th>
                        {tableFields.map(f => <th key={f.key} className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">{f.label}</th>)}
                        <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Staff</th>
                        <th className="px-3 py-2.5" />
                      </tr></thead>
                      <tbody className="bg-white divide-y divide-slate-50">
                        {shown.map(e => (
                          <tr key={e.id} className="hover:bg-slate-50/60">
                            <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap font-medium">{fmtDT(entryMs(e))}</td>
                            {tableFields.map(f => <td key={f.key} className="px-3 py-2.5">{fmtVal(f, e.data?.[f.key])}</td>)}
                            <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{e.recorded_by_name || '—'}</td>
                            <td className="px-3 py-2.5 text-right whitespace-nowrap">
                              <button onClick={() => setForm({ entry: e })} aria-label="Edit entry" className="p-1 text-slate-400 hover:text-primary-600"><Edit2 size={13} /></button>
                              <button onClick={() => deleteEntry(e)} aria-label="Delete entry" className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {entries.length > 10 && <button onClick={() => setShowAll(p => !p)} className="text-xs text-primary-600 font-semibold">{showAll ? 'Show fewer' : `Show all ${entries.length}`}</button>}
                </>
              )}
            </>
          )}
        </div>
      )}

      {form && (
        <EntryFormPanel chart={chart} entry={form.entry} initial={form.initial}
          onClose={() => setForm(null)}
          onSaved={again => { if (!again) setForm(null); loadEntries(); onRefresh(); }} />
      )}
      {settings && <ChartSettingsModal chart={chart} onClose={() => setSettings(false)} onSaved={() => { setSettings(false); onRefresh(); }} />}
    </div>
  );
}

// ── Add charts drawer ─────────────────────────────────────────────────────────
function AddChartsPanel({ definitions, assignedDefIds, onAdd, onClose, adding }) {
  const [q, setQ] = useState('');
  const match = (d) => !q.trim() || `${d.name} ${d.description || ''}`.toLowerCase().includes(q.trim().toLowerCase());
  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Add charts">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-left">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div><h3 className="font-bold text-slate-900">Add charts</h3><p className="text-xs text-slate-500">One click adds a chart to this client</p></div>
          <button onClick={onClose} aria-label="Close" className="p-1.5 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 pt-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search charts" autoFocus
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {CATS.map(cat => {
            const defs = definitions.filter(d => d.category === cat && match(d));
            if (!defs.length) return null;
            const meta = catMeta(cat);
            return (
              <div key={cat}>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><meta.icon size={13} /> {meta.label}</p>
                <div className="space-y-2">
                  {defs.map(d => {
                    const on = assignedDefIds.has(d.id);
                    return (
                      <div key={d.id} className={`flex items-center gap-3 rounded-xl border p-3 ${on ? `${meta.tint} ${meta.ring}` : 'border-slate-200'}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800">{d.name}</p>
                          <p className="text-[11px] text-slate-500 line-clamp-2">{d.description}</p>
                        </div>
                        {on ? <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1"><CheckCircle size={13} /> Added</span>
                          : <button onClick={() => onAdd(d)} disabled={adding === d.id} className="text-xs font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg px-3 py-1.5 disabled:opacity-50">{adding === d.id ? 'Adding…' : 'Add'}</button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function ClientCharts() {
  const { id: clientId } = useParams();
  const record = useClientRecord(clientId);
  const [definitions, setDefs] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState(false);
  const [adding, setAdding] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      const [d, a] = await Promise.all([api.get('/charts/definitions'), api.get(`/clients/${clientId}/charts`)]);
      setDefs(d.data); setAssigned(a.data);
    } catch { toast.error('Could not load charts'); } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  async function add(def) {
    setAdding(def.id);
    try {
      const { data } = await api.post(`/clients/${clientId}/charts`, { chart_def_id: def.id });
      toast.success(`${def.name} added`);
      await load(true);
      setOpenId(data?.id || null);
    } catch (err) { toast.error(err.response?.data?.message || 'Could not add the chart'); } finally { setAdding(null); }
  }
  async function removeChart(id) {
    try { await api.delete(`/clients/${clientId}/charts/${id}`); toast.success('Chart removed'); setAssigned(p => p.filter(c => c.id !== id)); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not remove the chart'); }
  }

  const assignedDefIds = useMemo(() => new Set(assigned.map(c => c.chart_def_id)), [assigned]);
  const statuses = useMemo(() => Object.fromEntries(assigned.map(c => [c.id, dueStatus(c.frequency, Number(c.last_entry_ms) || null)])), [assigned]);
  const dueCount = assigned.filter(c => statuses[c.id]?.due).length;
  const today = assigned.filter(c => c.last_entry_ms && sameDay(Number(c.last_entry_ms), Date.now())).length;
  const visible = assigned.filter(c => filter === 'all' || (filter === 'due' ? statuses[c.id]?.due : c.category === filter));
  // Medications first, then anything due, then the rest by category.
  const ordered = visible.slice().sort((a, b) =>
    (b.slug === 'medication_administration') - (a.slug === 'medication_administration')
    || (statuses[b.id]?.overdue ? 2 : statuses[b.id]?.due ? 1 : 0) - (statuses[a.id]?.overdue ? 2 : statuses[a.id]?.due ? 1 : 0)
    || CATS.indexOf(a.category) - CATS.indexOf(b.category) || (a.sort_order || 0) - (b.sort_order || 0));

  if (loading) {
    return (
      <ClientFrame record={record}>
        <div className="flex items-center justify-center py-24 gap-2 text-slate-500" aria-busy="true"><RefreshCw size={18} className="animate-spin" aria-hidden /><span>Loading charts…</span></div>
      </ClientFrame>
    );
  }

  const chip = (key, label, count) => (
    <button key={key} onClick={() => setFilter(key)}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
      {label}{count != null ? ` (${count})` : ''}
    </button>
  );

  return (
    <ClientFrame record={record}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><BarChart2 size={18} className="text-primary-500" /> Charts</h2>
            <p className="text-xs text-slate-500">
              {assigned.length} chart{assigned.length === 1 ? '' : 's'}
              {dueCount > 0 && <span className="text-amber-700 font-semibold"> · {dueCount} due</span>}
              {` · ${today} recorded today`}
            </p>
          </div>
          <button onClick={() => setPanel(true)} className="btn-primary flex items-center gap-2"><Plus size={15} /> Add charts</button>
        </div>

        {assigned.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {chip('all', 'All', assigned.length)}
            {dueCount > 0 && chip('due', 'Due now', dueCount)}
            {CATS.filter(c => assigned.some(a => a.category === c)).map(c => chip(c, catMeta(c).label, assigned.filter(a => a.category === c).length))}
          </div>
        )}

        {assigned.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
            <BarChart2 size={44} className="mx-auto text-slate-200 mb-3" />
            <h3 className="text-lg font-bold text-slate-700 mb-1">No charts yet</h3>
            <p className="text-sm text-slate-500 max-w-sm mx-auto mb-5">Add the charts this client needs: medications, vital signs, food and fluid, behaviour and more. Workers fill them in on shift in the Rostery app.</p>
            <button onClick={() => setPanel(true)} className="btn-primary inline-flex items-center gap-2"><Plus size={15} /> Add charts</button>
          </div>
        ) : (
          <div className="space-y-3">
            {ordered.map(c => (
              <ChartCard key={c.id} chart={c} open={openId === c.id} onToggle={() => setOpenId(o => (o === c.id ? null : c.id))}
                onRemove={() => removeChart(c.id)} onRefresh={() => load(true)} />
            ))}
            {ordered.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No charts match this filter.</p>}
          </div>
        )}
      </div>

      {panel && <AddChartsPanel definitions={definitions} assignedDefIds={assignedDefIds} adding={adding} onAdd={add} onClose={() => setPanel(false)} />}
    </ClientFrame>
  );
}
